import { useMemo } from 'react';
import useStateView from './useStateView'
import { activePaymentPlansView } from '../state/views';
import {
  isPromiseFullBalance as checkPromiseFullBalance,
  isPromisePlan,
} from '../utils/promisePaymentPlan';

// A hook that returns a small set of useful flags for determining the path a user
// can take through certain portal pages
const useActivePaymentPlanStatus = () => {
  const [ activePaymentPlans ] = useStateView(activePaymentPlansView);
  const hasLoaded = activePaymentPlans !== null && activePaymentPlans !== undefined;
  const plans = hasLoaded ? activePaymentPlans?.paymentPlans || [] : [];
  const isLoading = !hasLoaded;

  // does the account have NO active plans?
  const hasNoActivePlans = useMemo(
    () => hasLoaded && !(plans?.length > 0),
    [ hasLoaded, plans ]
  );

  const {
    hasPromise,
    hasNonPromisePlans,
    promisePlan,
    isPromise,
  } = useMemo(
    () => {
      if (!hasLoaded) {
        return {
          hasPromise: false,
          hasNonPromisePlans: false,
          promisePlan: null,
          isPromise: false,
        };
      }

      const promisePlan = plans.find(isPromisePlan);
      return {
        hasPromise: !!promisePlan,
        hasNonPromisePlans: plans.some((plan) => !isPromisePlan(plan)),
        promisePlan,
        isPromise: !!promisePlan,
      };
    },
    [ hasLoaded, plans ]
  );

  // can the user create a new payment plan?
  const canCreatePlan = hasLoaded && (hasNoActivePlans || !hasNonPromisePlans);

  const hasPromiseOnly = hasLoaded && hasPromise && !hasNonPromisePlans;

  const upcomingPromiseInstallments = useMemo(() => {
    if (!promisePlan?.paymentSchedule) return 0;
    return promisePlan.paymentSchedule.filter((payment) => {
      if (!payment?.paymentStatus) return true;
      return ['PENDING', 'SCHEDULED'].includes(payment.paymentStatus);
    }).length;
  }, [promisePlan]);

  const isPromisePaymentPlan = hasPromiseOnly && upcomingPromiseInstallments > 1;
  const isPromiseOneTime = hasPromiseOnly && upcomingPromiseInstallments === 1;

  const isPromiseFullBalance = useMemo(
    () => hasPromiseOnly && checkPromiseFullBalance(promisePlan),
    [ hasPromiseOnly, promisePlan ]
  );

  const isPromisePartialOneTime = isPromiseOneTime && !isPromiseFullBalance;
  const canShowOffersHome = canCreatePlan && !hasPromiseOnly;
  const shouldLoadSettlementOnHome =
    canShowOffersHome || (hasPromiseOnly && isPromiseFullBalance);

  return {
    isLoading,
    canCreatePlan,
    canShowOffersHome,
    shouldLoadSettlementOnHome,
    hasPromise,
    hasPromiseOnly,
    hasNoActivePlans,
    hasNonPromisePlans,
    promisePlan,
    isPromise,
    isPromisePaymentPlan,
    isPromiseOneTime,
    isPromiseFullBalance,
    isPromisePartialOneTime,
    upcomingPromiseInstallments,
  };
};

export default useActivePaymentPlanStatus;
